import { Configuration, UsersApi } from '@/api';
import useGetUserQuery from '@/api/queries/useGetUserQuery';
import Button from '@/components/Button';
import Confirm from '@/components/Confirm';
import CustomToast from '@/components/CustomToast';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import PageHeader from '@/components/PageHeader';
import { User } from '@/models/User';
import Breadcrumbs from '@/modules/admin/components/Breadcrumbs';
import UserForm from '@/modules/admin/components/UserForm';
import { UserListPath } from '@/modules/admin/paths';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

const UserEdit = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { isLoading, user, invalidateQuery } = useGetUserQuery(userId);

  const usersApi = new UsersApi(new Configuration(useAuth));

  const handleOnSubmit = async (updatedUser: User): Promise<void> => {
    if (!updatedUser.id) return;

    try {
      await usersApi.updateUser(updatedUser.id, updatedUser);

      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={`User "${updatedUser.fullName}" updated successfully!`}
          visible={t.visible}
        />
      ));

      await invalidateQuery();
      navigate(UserListPath);
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error('Failed to update user');
    }
  };

  const handleOnDelete = async () => {
    if (!user?.id) return;

    try {
      await usersApi.deleteUser(user.id);

      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={`User "${user.fullName}" / "${user.username}" deleted successfully!`}
          visible={t.visible}
        />
      ));

      navigate(UserListPath);
    } catch (error) {
      console.error('Failed to delete user:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <SpinnerIcon />
      </div>
    );
  }

  if (!user) return null;

  return (
    <>
      <Breadcrumbs />

      <PageHeader title="Edit User">
        <Confirm
          onConfirm={handleOnDelete}
          title="Delete User"
          message={`Are you sure you want to delete user "${user.fullName}" (username: "${user.username}")? This action cannot be undone.`}
          danger
        >
          {(handleClick, loading) => (
            <Button
              onClick={handleClick}
              icon={<XMarkIcon className="h-5 w-5 mr-1" aria-hidden="true" />}
              loading={loading}
              variant="delete"
            >
              Delete User
            </Button>
          )}
        </Confirm>
      </PageHeader>

      <div className="w-full bg-white mx-auto rounded-lg shadow p-6 lg:p-12">
        <UserForm onSubmit={handleOnSubmit} user={user} />
      </div>
    </>
  );
};

export default UserEdit;
